import React, { useState } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { NSCard, NSButton } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import RenderIf from 'components/RenderIf/RenderIf';
import { CalculatedSubline, SaveInput, UsesCardEditContext, UsesCardEditVariant } from 'views/ProFormaTable/types';

import './UsesCardEditView.scss';

interface Props {
    id: string;
    title: string;
    total: number | null | undefined;
    costPerRSF: number | null | undefined;
    variant: UsesCardEditVariant;
    context?: UsesCardEditContext;
    initialMode?: 'dollar' | 'percentage';
    calculatedSublines?: CalculatedSubline[];
    isLastCard?: boolean;
    onSave: (input: SaveInput) => void;
    onCancel: () => void;
}

const UsesCardEditView = ({
    id,
    title,
    total,
    costPerRSF,
    variant,
    context,
    initialMode = 'dollar',
    calculatedSublines,
    isLastCard = false,
    onSave,
    onCancel,
}: Props) => {
    const [draftMode, setDraftMode] = useState<'dollar' | 'percentage'>(initialMode);

    const hasPercentMode = variant !== 'dollar-only';
    const percentButtonLabel = variant === 'dollar-or-pct-of-debt' ? '% of loan' : '% Relative';
    const contextLabel = variant === 'dollar-or-pct-of-debt' ? 'of debt amount' : 'of hard costs';
    const contextBase = variant === 'dollar-or-pct-of-debt' ? context?.debtAmount : context?.hardTotal;
    const canUsePercentMode = hasPercentMode && contextBase != null && contextBase > 0;
    const effectiveMode = canUsePercentMode ? draftMode : 'dollar';
    const contextDollars = effectiveMode === 'percentage' && context?.initialPercent != null && contextBase != null
        ? contextBase * context.initialPercent
        : null;

    const handleHeroDollarCommit = (updatedValue: string | number) => {
        const numeric = Number(updatedValue);
        if (!Number.isFinite(numeric)) return;
        onSave({ mode: 'dollar', value: numeric });
    };

    const handleHeroPercentCommit = (updatedValue: string | number) => {
        const decimal = Number(updatedValue);
        if (!Number.isFinite(decimal)) return;
        const percent = decimal * 100;
        if (variant === 'dollar-or-pct-of-hard') {
            if (context?.hardTotal == null) return;
            onSave({ mode: 'soft-pct', percent, hardTotal: context.hardTotal });
            return;
        }
        if (variant === 'dollar-or-pct-of-debt') {
            onSave({ mode: 'financing-pct', percent });
        }
    };

    const handleCostPerRSFCommit = (updatedValue: string | number) => {
        const numeric = Number(updatedValue);
        if (!Number.isFinite(numeric)) return;
        onSave({ mode: 'per-rsf', value: numeric });
    };

    const handleCancelMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onCancel();
    };

    return (
        <NSCard
            id={id}
            className={classNames('UsesCard NSCard--level-5 d-flex flex-column border-primary p-3', { 'mr-3': !isLastCard })}
        >
            <div className="d-flex align-items-start justify-content-between mb-1">
                <div className="d-flex flex-column flex-grow-1">
                    <div className="text-dark mb-2">{title}</div>
                    <RenderIf isTrue={hasPercentMode}>
                        <div className="d-flex mb-1 w-100" role="group" aria-label="Input mode">
                            <NSButton
                                text="$ Total"
                                ariaLabel="Dollar input mode"
                                ariaPressed={effectiveMode === 'dollar'}
                                allowReadOnlyAccess
                                className={classNames('font-12 font-weight-bold text-nowrap text-dark w-100', {
                                    'bg-primary border-primary': effectiveMode === 'dollar',
                                    'bg-transparent': effectiveMode !== 'dollar',
                                })}
                                callback={() => setDraftMode('dollar')}
                            />
                            <NSButton
                                text={percentButtonLabel}
                                ariaLabel="Percentage input mode"
                                ariaPressed={effectiveMode === 'percentage'}
                                allowReadOnlyAccess
                                disabled={!canUsePercentMode}
                                className={classNames('font-12 font-weight-bold text-nowrap text-dark w-100', {
                                    'bg-primary border-primary': effectiveMode === 'percentage',
                                    'bg-transparent': effectiveMode !== 'percentage',
                                })}
                                callback={() => setDraftMode('percentage')}
                            />
                        </div>
                    </RenderIf>
                    {effectiveMode === 'dollar' ? (
                        <NSCellInput
                            key="dollar"
                            type="number"
                            numberType="currency"
                            customClassName="w-100"
                            inputHeight="36px"
                            inputElementClassName="font-24 font-weight-semibold"
                            labelClassName="font-24 font-weight-semibold"
                            placeholder="$0"
                            value={total}
                            handleUpdateValue={handleHeroDollarCommit}
                        />
                    ) : (
                        <NSCellInput
                            key="percentage"
                            type="number"
                            numberType="percentage"
                            customClassName="w-100"
                            inputHeight="36px"
                            inputElementClassName="font-24 font-weight-semibold"
                            labelClassName="font-24 font-weight-semibold"
                            placeholder="0.00%"
                            value={context?.initialPercent ?? null}
                            handleUpdateValue={handleHeroPercentCommit}
                        />
                    )}
                </div>
                <NSButton
                    icon={faXmark}
                    size="sm"
                    ariaLabel={`Cancel editing ${title}`}
                    className="p-1"
                    callback={onCancel}
                    onMouseDown={handleCancelMouseDown}
                />
            </div>
            <RenderIf isTrue={effectiveMode === 'percentage'}>
                <div className="font-12">
                    <span>
                        {contextLabel}
                        {contextDollars != null ? ` · ${numeralFormatterCurrency(contextDollars, '$0,0')}` : ''}
                    </span>
                </div>
            </RenderIf>
            <div className="d-flex align-items-center font-12">
                <div className="UsesCardEditView__rsf-input">
                    <NSCellInput
                        type="number"
                        numberType="currency"
                        inputElementClassName="font-12"
                        labelClassName="font-12"
                        placeholder="$0.00"
                        value={costPerRSF}
                        handleUpdateValue={handleCostPerRSFCommit}
                        showCents
                    />
                </div>
                <span className="ml-1">/ RSF</span>
            </div>
            {calculatedSublines?.map(({ label, amount }) => (
                amount != null && (
                    <div key={label} className="font-12">
                        {`${numeralFormatterCurrency(amount, '$0,0')} ${label}`}
                    </div>
                )
            ))}
        </NSCard>
    );
};

export default UsesCardEditView;
